import {isExternalLink, useHistory} from '@aboutyou/router';
import {styled} from '@linaria/react';
import React from 'react';
import {COLORS} from '../../../constants/colors';
import {RowSection} from '../../molecules/SplitView/RowSection';

interface OwnProps {
  bannerLink?: string;
  dataTestid: string;
}

interface BannerContainerProps {
  backgroundImage?: string;
  backgroundColor?: string;
  color?: string;
  className?: string;
  style?: React.CSSProperties;
  small?: boolean;
  increaseClickArea?: boolean;
  onBannerClick?: () => void;
}

const FALLBACK_BACKGROUND_COLOR = COLORS.RedSale;

export type BannerProps = OwnProps & BannerContainerProps;

export const Banner: React.FC<React.PropsWithChildren<BannerProps>> = ({
  color,
  backgroundImage,
  backgroundColor,
  children,
  bannerLink,
  className,
  style,
  dataTestid,
  small,
  increaseClickArea,
  onBannerClick,
}) => {
  const wrappedChildren = <RowSection>{children}</RowSection>;
  const history = useHistory();
  const onClick = () => {
    onBannerClick && onBannerClick();

    if (bannerLink && isExternalLink(bannerLink)) {
      window.open(bannerLink, '_blank');
    } else if (bannerLink) {
      history.push(bannerLink);
    }
  };
  return (
    <Container
      className={className}
      style={style}
      data-testid={dataTestid}
      color={color}
      $backgroundColor={backgroundColor || FALLBACK_BACKGROUND_COLOR}
      $backgroundImage={backgroundImage}
      $increaseClickArea={!!increaseClickArea}
      $pointer={!!bannerLink}
      $small={small}
      onClick={onClick}
    >
      {increaseClickArea ? wrappedChildren : children}
    </Container>
  );
};

const Container = styled.header<
  BannerContainerProps & {
    $backgroundImage: string | undefined;
    $backgroundColor: string;
    $increaseClickArea: boolean;
    /**
     * This property sets the min-height down to 1rem to have a smaller banner
     */
    $small?: boolean;
    $pointer?: boolean;
  }
>`
  cursor: ${({$pointer}) => ($pointer ? 'pointer' : 'default')};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: ${({$increaseClickArea}) =>
    $increaseClickArea ? '0' : '1.125rem 0.75rem;'};
  min-height: 2rem;
  max-height: 14rem;
  overflow: hidden;

  padding: ${({$small, $increaseClickArea}) => {
    if (!$small) {
      return '';
    }

    return $increaseClickArea ? '0' : '0.75rem 0.75rem';
  }};

  color: ${({color}) => color ?? ''};
  background-color: ${({$backgroundColor}) => $backgroundColor ?? ''};

  background-image: ${({$backgroundImage}) =>
    $backgroundImage ? `url(${$backgroundImage})` : ''};
  background-position: ${({$backgroundImage}) =>
    $backgroundImage ? 'center' : ''};
  background-size: ${({$backgroundImage}) => ($backgroundImage ? 'cover' : '')};
`;
